// Core packages

// Third party packages

// Custom packages
import {
  DocumentArchiveContractFile,
  DocumentArchiveContractOccurrence,
} from "src/app/modules/properties/components/document-archive-cancel-contract-dialog/document-archive-cancel-contract-dialog.component";
import {
  ArchiveSameCodeRow,
  isPaperArchiveRow,
} from "src/app/modules/properties/document-archive-edit.utils";
import { labelOf } from "src/app/modules/properties/document-archive-insert.utils";
import { isArchiveRowPrintable } from "src/app/modules/properties/document-archive-print.utils";
import {
  DocumentArchiveDocListItem,
  DocumentArchiveTreeArea,
  DocumentArchiveTreeContent,
  DocumentArchiveTreeGroup,
  DocumentArchiveTreeRow,
} from "src/app/shared/interfaces/document-archive-doc.interface";

/**
 * Pure helpers of the menu of a row of the 3.0.0 archive (`documentsV3`): who is offered which
 * action, the executor column, and the occurrences of a document the delete and the contract actions
 * work from.
 *
 * They were private methods of the archive page. They live here so that every screen showing the
 * rows of that archive — the archive itself, the search within one property and the general search —
 * applies the same rules, and so that the rules can be tested without Angular. The dialogs they feed
 * are opened by @see DocumentArchiveRowActionsService.
 *
 * Every input is read off the tree as `GET /document-archive-docs/tree` serves it: nothing here
 * calls the back-end, sorts, counts or decides what is present.
 *
 * @since 3.0.0
 */

/**
 * Role types allowed to cancel or expire a contract.
 *
 * The same four that read the notes of the mapping on the archive page, and for a similar reason:
 * the legacy dossier hides both entries from `temporary` and `shared` (`!loggedUserTemporary &&
 * !loggedUserShared` on top of the permission), because those two read a dossier from OUTSIDE — an
 * occasional visitor, a technician invited onto one property. Writing to a supplier on the
 * administrator's behalf and ending a contract is not theirs to do, and the back-end does not stop
 * them: it only asks for the permission.
 *
 * Expiring asks for a narrower list. @see CONTRACT_EXPIRE_ROLE_TYPES
 */
const CONTRACT_ACTION_ROLE_TYPES = ["admin", "operator", "master", "collaborator"];

/**
 * Role types allowed to EXPIRE a contract ("Disattiva il contratto").
 *
 * The contract-action roles without `master`: the administrator keeps the cancellation but does not
 * switch a contract off, and the legacy dossiers hide that entry from it as well. `collaborator`
 * keeps it on purpose, although everywhere else on the archive page it moves together with `master`.
 *
 * Hiding the entry is not the authorization: the endpoint has to refuse `master` too.
 */
const CONTRACT_EXPIRE_ROLE_TYPES = ["admin", "operator", "collaborator"];

/**
 * Role types the search within ONE property (`/properties/:propertyId/searchV3`) offers a REDUCED
 * row menu: "Scarica file", "Qr-code" and "Invia disdetta", nothing else.
 *
 * The legacy search page does the same (`loggedUserMaster`, true for both): "Visualizza dati
 * aggiuntivi", "Modifica dati aggiuntivi", "Elimina documento" and "Disattiva il contratto" are
 * hidden from them there whatever they hold, and "Stampa modulo doc" does not exist there at all.
 * They keep all of it on the archive page, so hiding it here is not the authorization.
 *
 * A deny list, as in the legacy: every other role type keeps the menu of the archive page.
 */
const PROPERTY_SEARCH_REDUCED_MENU_ROLE_TYPES = ["master", "collaborator"];

/**
 * Shared empty list returned when a document has no attachment.
 *
 * A fresh `[]` would be a new reference on every change detection pass, and `*ngFor` would diff it
 * again for every row of the table at every pass.
 */
const NO_FILES: any[] = [];

/**
 * A row of the archive tree together with where it sits: what a screen needs to open the menu of a
 * row it did not render out of the tree itself — the general search finds a row by its id.
 *
 * The same shape as the rows of the same-code selection, on purpose: both are "a row and its place".
 *
 * @since 3.0.0
 */
export type ArchiveRowLocation = ArchiveSameCodeRow;

/** The supplier names resolved so far, by supplier id */
export interface ArchiveSupplierNames {
  [supplierId: string]: string;
}

/**
 * Whether a row is the copy of a contract served inside the aggregated contracts section.
 *
 * Only that section carries the fields the back-end resolves for its table, so their PRESENCE is the
 * marker — `executorName` above all, which is a string there, possibly empty, and absent everywhere
 * else.
 */
function isAggregatedCopy(row: DocumentArchiveTreeRow): boolean {
  return typeof row.executorName === "string";
}

/** A name of the map, read as an own entry only */
function supplierNameOf(names: ArchiveSupplierNames, supplierId: string): string {
  if (!names || !supplierId || !Object.prototype.hasOwnProperty.call(names, supplierId)) {
    return "";
  }
  return names[supplierId] ? names[supplierId] : "";
}

/**
 * Heading of a block.
 *
 * `translation` first, `label` after: a block standing for a value (a conductor, a supplier, a
 * mapping element) carries the literal string the back-end resolved, while a block standing for a
 * node of the taxonomy carries a localized pair. Reading `translation` first is what lets the
 * back-end nest the taxonomy under a block without having to know the language of the request.
 *
 * @since 3.0.0
 *
 * @param group The block
 * @param lang  Language of the page
 */
export function archiveGroupLabel(
  group: DocumentArchiveTreeGroup | null | undefined,
  lang: string
): string {
  if (!group) {
    return "";
  }
  if (group.translation) {
    return labelOf(group.translation, lang);
  }
  return group.label ? group.label : "";
}

/**
 * The files attached to a document, as stored in `data.document[]`.
 *
 * `data.documentFile[]` is deliberately ignored: it is the name the front-end uses to SEND the temp
 * files, and where it survives stored it is the copy carrying the malformed paths.
 *
 * @since 3.0.0
 *
 * @param row The document
 */
export function archiveAttachedFiles(row: DocumentArchiveDocListItem | null | undefined): any[] {
  if (!row || !row.data || !Array.isArray(row.data.document)) {
    return NO_FILES;
  }
  return row.data.document;
}

/**
 * Whether the menu of a row has any entry for this viewer.
 *
 * Only the documents actually uploaded carry one (`present`). A guest keeps the download alone, as in
 * the legacy dossier: on a row with no file nothing is left for them, and a button opening an empty
 * menu reads as a broken one. A row the cascade set aside with its mapping element gives a guest no
 * menu either, although the back-end serves its files since EXT-08: guests never reach such a row —
 * the dossier reveals it only through the "removed structures" filter of the actions menu, which is
 * hidden from them, and the search lists none — so the condition only keeps the rule closed if one
 * ever did.
 *
 * @since 3.0.0
 *
 * @param row             The row
 * @param documentActions Whether the viewer acts on documents beyond the download
 */
export function hasArchiveRowMenu(
  row: DocumentArchiveTreeRow | null | undefined,
  documentActions: boolean
): boolean {
  if (!row || !row.present) {
    return false;
  }
  if (documentActions) {
    return true;
  }
  return !row.deleted && archiveAttachedFiles(row).length > 0;
}

/**
 * Whether the user can archive a new document, or change the files of one.
 *
 * The permission, and a role that is not a guest: the legacy hides every insert from `temporary` and
 * `shared` whatever they hold (@see DocumentArchiveVisibility.documentActions).
 *
 * @since 3.0.0
 *
 * @param documentActions Whether the viewer acts on documents beyond the download
 * @param permissions     Permissions of the active role
 */
export function canInsertArchiveDocuments(
  documentActions: boolean,
  permissions: string[]
): boolean {
  return (
    documentActions &&
    Array.isArray(permissions) &&
    permissions.indexOf("canCreateDocuments") > -1
  );
}

/**
 * Whether "Modifica dati aggiuntivi" belongs on the menu of a row, as far as the viewer goes.
 *
 * BOTH permissions, and neither is redundant:
 *  - `canEditDocumentsData` is what every legacy screen asks for this entry. It is the switch
 *    «Modifica campi aggiuntivi documenti» of the user page applied to the role
 *    (@see AuthService.manageGeneralPermission), and the back-end never reads it;
 *  - `canCreateDocuments` is what the back-end enforces on `POST /update-docs`: without it the entry
 *    opens a form whose save can only answer 403.
 *
 * The state of the row (filed, not deleted) is the caller's: each screen reads it its own way.
 *
 * @since 3.0.0
 *
 * @param documentActions Whether the viewer acts on documents beyond the download
 * @param permissions     Permissions of the active role
 */
export function canEditArchiveDocumentData(
  documentActions: boolean,
  permissions: string[]
): boolean {
  if (!documentActions || !Array.isArray(permissions)) {
    return false;
  }
  return (
    permissions.indexOf("canEditDocumentsData") > -1 &&
    permissions.indexOf("canCreateDocuments") > -1
  );
}

/**
 * The role types that change the parts of a paper document: platform staff, who file the paper and
 * print its labels (FEX-08; the paper insert asks the same).
 */
const PAPER_PARTS_ROLE_TYPES = ["admin", "operator"];

/**
 * Whether «Modifica parti» belongs on the menu of a row (FEX-08): the parts of a paper document, the
 * folders it arrived in, each with its own label.
 *
 *  - only once the 3.0.0 archive is live: before the switch the call would change the delivery note
 *    entry the legacy shares, while the rows of this archive are realigned on the legacy every night;
 *  - a filed paper document, not deleted, with its code;
 *  - admin and operator only, as the back-end enforces;
 *  - the two permissions of «Modifica dati aggiuntivi» (@see canEditArchiveDocumentData): the
 *    back-end asks the same on `POST /paper-parts`, the parts being additional data of the paper.
 *
 * Hiding the entry is not the authorization: the back-end refuses that user too.
 *
 * @since 3.0.0
 *
 * @param row             The row
 * @param roleType        Type of the active role
 * @param documentActions Whether the viewer acts on documents beyond the download
 * @param permissions     Permissions of the active role
 * @param live            Whether the 3.0.0 archive is live (@see isArchiveV3Live)
 */
export function canEditArchivePaperParts(
  row: DocumentArchiveTreeRow | null | undefined,
  roleType: string,
  documentActions: boolean,
  permissions: string[],
  live: boolean
): boolean {
  if (!live || !row || row.deleted || !row.present) {
    return false;
  }
  if (PAPER_PARTS_ROLE_TYPES.indexOf(roleType) === -1) {
    return false;
  }
  if (!canEditArchiveDocumentData(documentActions, permissions)) {
    return false;
  }
  const barCode = row.data && typeof row.data.barCode === "string" ? row.data.barCode.trim() : "";
  return barCode !== "" && isPaperArchiveRow(row);
}

/**
 * Whether "Elimina documento" belongs on the menu of a row, as far as the viewer goes.
 *
 * BOTH permissions, as for the contract actions (@see canActOnArchiveContract):
 *  - `canDeleteDocuments` is what every legacy screen asks for this entry. It is the switch
 *    «Eliminare documenti (cestino)» of the user page applied to the role, and the back-end never
 *    reads it: a user who holds `canCreateDocuments` with that switch off is refused the entry
 *    everywhere else in the platform, and granting it here would not fail loudly — the document
 *    would really go to the bin;
 *  - `canCreateDocuments` is what the back-end enforces on `DELETE /bulk`.
 *
 * Hiding the entry is not the authorization: the back-end has to refuse that user too.
 * The state of the row (filed, not deleted) is the caller's.
 *
 * @since 3.0.0
 *
 * @param documentActions Whether the viewer acts on documents beyond the download
 * @param permissions     Permissions of the active role
 */
export function canDeleteArchiveDocument(
  documentActions: boolean,
  permissions: string[]
): boolean {
  if (!documentActions || !Array.isArray(permissions)) {
    return false;
  }
  return (
    permissions.indexOf("canDeleteDocuments") > -1 &&
    permissions.indexOf("canCreateDocuments") > -1
  );
}

/**
 * Whether the search within ONE property offers this role the whole menu of the archive page, or
 * the reduced one of the legacy search. @see PROPERTY_SEARCH_REDUCED_MENU_ROLE_TYPES
 *
 * @since 3.0.0
 *
 * @param roleType Type of the active role
 */
export function hasFullPropertySearchMenu(roleType: string): boolean {
  return PROPERTY_SEARCH_REDUCED_MENU_ROLE_TYPES.indexOf(roleType) === -1;
}

/**
 * Whether the two contract actions belong on a row: "Disdetta", and the first half of "Disattiva il
 * contratto".
 *
 * The state part is the legacy one: only a CONTRACT, only a row that is not the debris of a removed
 * mapping element, and only one that has not expired — there is nothing to cancel or to expire on a
 * contract that has already ended. Never a VIRTUAL document (`mockup`, FE-M5): the legacy dossier
 * offers no contract action on one, and the back-end skips it on both routes («mockup») — the
 * cancellation mail would reach the supplier while nothing is recorded.
 *
 * The permission part asks for BOTH, and neither is redundant. `canCreateDocuments` is what the
 * back-end enforces, so without it the button would only produce a 403. `canDeleteDocuments` is what
 * every legacy dossier asks for these very two entries, and the two permissions are INDEPENDENT
 * switches: an operator allowed to archive but not to delete is refused these actions everywhere
 * else in the platform, and granting them here would not fail loudly — the call would succeed, the
 * contract would really expire and the message would really go out.
 *
 * The role part is the legacy one too, and the back-end enforces none of it.
 * @see CONTRACT_ACTION_ROLE_TYPES
 *
 * @since 3.0.0
 *
 * @param row         The row
 * @param roleType    Type of the active role
 * @param permissions Permissions of the active role
 */
export function canActOnArchiveContract(
  row: DocumentArchiveTreeRow | null | undefined,
  roleType: string,
  permissions: string[]
): boolean {
  if (!row || !row.contract || row.deleted || row.expired || row.mockup === true) {
    return false;
  }
  if (CONTRACT_ACTION_ROLE_TYPES.indexOf(roleType) === -1) {
    return false;
  }
  const granted = Array.isArray(permissions) ? permissions : [];
  return (
    granted.indexOf("canCreateDocuments") > -1 &&
    granted.indexOf("canDeleteDocuments") > -1
  );
}

/**
 * Whether "Disattiva il contratto" belongs on a row.
 *
 * Everything `canActOnArchiveContract` asks, on a narrower role list: the cancellation stays with
 * `master`, the switch-off does not. @see CONTRACT_EXPIRE_ROLE_TYPES
 *
 * @since 3.0.0
 *
 * @param row         The row
 * @param roleType    Type of the active role
 * @param permissions Permissions of the active role
 */
export function canExpireArchiveContract(
  row: DocumentArchiveTreeRow | null | undefined,
  roleType: string,
  permissions: string[]
): boolean {
  return (
    canActOnArchiveContract(row, roleType, permissions) &&
    CONTRACT_EXPIRE_ROLE_TYPES.indexOf(roleType) > -1
  );
}

/**
 * Whether "Stampa modulo doc" is offered on a row.
 *
 * As in the legacy dossier: every filed document, contracts included, and never to the guests. On top
 * of it, none of the rows the print dialog cannot handle:
 *  - a row with no barcode, where the legacy shows the entry and the dialog then answers "No qrCodes
 *    to inject";
 *  - a row with no date the dialog can read (@see archivePrintUploadDate).
 *
 * A row the cascade set aside with its mapping element is printed too, as in the legacy, which offers
 * the entry on the removed structures as well: the dialog reads the row itself and, for a paper
 * document, the delivery note by code, and neither depends on the row being live.
 *
 * Whether the document is filed is read from `present`, the back-end rule; the barcode test of
 * `isArchiveRowPrintable` only guards the input of the dialog.
 *
 * @since 3.0.0
 *
 * @param row             The row
 * @param documentActions Whether the viewer acts on documents beyond the download
 */
export function canPrintArchiveModule(
  row: DocumentArchiveTreeRow | null | undefined,
  documentActions: boolean
): boolean {
  if (!row || !documentActions) {
    return false;
  }
  if (!row.present) {
    return false;
  }
  return isArchiveRowPrintable(row);
}

/**
 * Id of the supplier a document refers to, when it has one.
 *
 * Two sources, in this order:
 *  - `executorSupplierId`, resolved by the back-end on the rows of the contracts section. The field
 *    BEING THERE ends the search, `null` included: `null` says the document names an executor who is
 *    not in the registry, which is an answer and not a missing value;
 *  - `data.ragioneSocialeFornitoreId` everywhere else. Only the document types whose form carries the
 *    supplier autocomplete store it. For all the others the document refers to a subject that is not
 *    in the suppliers registry (the tenant, the ASL, whoever signed it), and there is no id at all.
 *
 * @since 3.0.0
 *
 * @param row The row
 */
export function archiveExecutorSupplierId(row: DocumentArchiveTreeRow | null | undefined): string {
  if (!row) {
    return "";
  }
  if (row.executorSupplierId !== undefined) {
    return row.executorSupplierId ? row.executorSupplierId : "";
  }
  if (!row.data || !row.data.ragioneSocialeFornitoreId) {
    return "";
  }
  return row.data.ragioneSocialeFornitoreId;
}

/**
 * Name to show in the executor column.
 *
 * Priority: the name resolved from the suppliers registry, then the copy stored in the document. The
 * stored copy is only a FALLBACK, used when the id is missing (most document types have no supplier)
 * or when the lookup did not answer — a name that cannot be refreshed is still better than an empty
 * cell.
 *
 * Returns an empty string when nothing is known, and the template shows the placeholder.
 *
 * On the rows of the contracts section the back-end sends the name ALREADY RESOLVED, and that one
 * wins over everything below: it reads the registry when the row names a supplier and a twelve field
 * chain of `data` when it does not, so an empty `executorName` means "nobody" and NOT "look into
 * `data` yourself". Recomposing it here would be a second, shorter and different implementation of
 * that chain.
 *
 * @since 3.0.0
 *
 * @param row           The row
 * @param supplierNames The names resolved so far, by supplier id
 */
export function archiveExecutorName(
  row: DocumentArchiveTreeRow | null | undefined,
  supplierNames: ArchiveSupplierNames
): string {
  if (row && typeof row.executorName === "string") {
    return row.executorName;
  }

  const resolved = supplierNameOf(supplierNames, archiveExecutorSupplierId(row));
  if (resolved) {
    return resolved;
  }

  const data = row && row.data ? row.data : undefined;
  if (!data) {
    return "";
  }
  if (data.ragioneSocialeFornitore) {
    return data.ragioneSocialeFornitore;
  }
  // Second hidden companion of the supplier autocomplete, used by the fuel document types
  if (data.nomeFornitoreCombustibile) {
    return data.nomeFornitoreCombustibile;
  }
  return "";
}

/**
 * True when the executor can be opened as a supplier.
 *
 * Never for the guests (temporary, shared): the supplier card (`suppliers/detailv2`) asks for
 * `canCreateSuppliers`, which they never hold, so a link would only lead them to a refusal. They read
 * the name as plain text, where the legacy dossier links it for them anyway (FEX-29, Oct 2026).
 *
 * On the rows of the contracts section the gate is the back-end field: it resolved the id against the
 * registry, so a non null one is a supplier that exists. Nothing was checked about THIS user being
 * allowed to read it, which the lookup below happens to prove — a role that cannot open supplier
 * pages will get the error of that page, not a dead link here.
 *
 * Everywhere else the gate is "the lookup succeeded", which is stronger than checking a flag inside
 * `data`: it proves the supplier record exists AND is readable by this user.
 *
 * @since 3.0.0
 *
 * @param row             The row
 * @param supplierNames   The names resolved so far, by supplier id
 * @param documentActions Whether the viewer acts on documents beyond the download
 */
export function isArchiveExecutorLinked(
  row: DocumentArchiveTreeRow | null | undefined,
  supplierNames: ArchiveSupplierNames,
  documentActions: boolean
): boolean {
  if (!documentActions) {
    return false;
  }
  if (row && row.executorSupplierId !== undefined) {
    return !!row.executorSupplierId;
  }
  return !!supplierNameOf(supplierNames, archiveExecutorSupplierId(row));
}

/**
 * CSS class carrying the colour of a row, as described by the legend of the archive: green a
 * mandatory document filed, red a mandatory one missing, black an optional or an expired one.
 *
 * The rule of the archive page (`rowStatusClass` of `documentsV3`), and of the screens that list its
 * rows elsewhere. Every input is a back-end field of the ROW, `mandatory` included: it can differ
 * from the one of the document type, and the bands and the counters are computed on the row.
 *
 * A VIRTUAL document (`mockup`, FE-M5) is blue whatever its state, as in the legacy dossier, whose
 * class this is: drawn green, red or black it would read as a real document. Only the archive page
 * asks the tree for them (@see DocumentArchiveDocsService.getTreeByProperty).
 *
 * @since 3.0.0
 *
 * @param row The row
 */
export function archiveRowStatusClass(
  row:
    | Pick<DocumentArchiveDocListItem, "expired" | "mandatory" | "present" | "mockup">
    | null
    | undefined
): string {
  if (row && row.mockup === true) {
    return "document-wrapper-text-mockup";
  }
  if (!row || row.expired || !row.mandatory) {
    return "document-wrapper-text-not-mandatory";
  }
  return row.present
    ? "document-wrapper-text-mandatory-added"
    : "document-wrapper-text-mandatory-missing";
}

/**
 * Every row of the tree, in tree order.
 *
 * Includes the blocks hidden as disabled structures, on purpose: the supplier lookups run once per
 * load, so skipping them would leave those rows with the stale name copied in `data` for anyone who
 * turns the hidden blocks on afterwards.
 *
 * @since 3.0.0
 *
 * @param areas The tree
 */
export function collectArchiveRows(
  areas: DocumentArchiveTreeArea[] | null | undefined
): DocumentArchiveTreeRow[] {
  const rows: DocumentArchiveTreeRow[] = [];

  (Array.isArray(areas) ? areas : []).forEach((area) => {
    const contents = area && Array.isArray(area.content) ? area.content : [];
    contents.forEach((content) => {
      const groups = content && Array.isArray(content.groups) ? content.groups : [];
      groups.forEach((group) => {
        const types = group && Array.isArray(group.documents) ? group.documents : [];
        types.forEach((type) => {
          const archiveDocs = type && Array.isArray(type.archiveDocs) ? type.archiveDocs : [];
          archiveDocs.forEach((row) => rows.push(row));
        });
      });
    });
  });

  return rows;
}

/**
 * The supplier ids to resolve into current names, each once.
 *
 * Distinct ids, not one per row: an archive with two hundred rows referring to fifteen suppliers costs
 * fifteen calls, not two hundred. The rows of the contracts section are skipped: they arrive with the
 * name already resolved, and a lookup would spend a call to answer a question that is answered.
 *
 * @since 3.0.0
 *
 * @param rows The rows on screen
 */
export function archiveSupplierIdsToResolve(rows: DocumentArchiveTreeRow[]): string[] {
  const distinctIds: string[] = [];
  const seen: { [supplierId: string]: boolean } = Object.create(null);

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || isAggregatedCopy(row)) {
      return;
    }
    const supplierId = archiveExecutorSupplierId(row);
    if (!supplierId || seen[supplierId]) {
      return;
    }
    seen[supplierId] = true;
    distinctIds.push(supplierId);
  });

  return distinctIds;
}

/**
 * A stored file path reduced to what goes after the API host.
 *
 * Idempotent on purpose: it removes a `scheme://host` prefix wherever it appears, turns backslashes
 * into slashes and drops the leading ones. Given the clean relative path the back-end now serves it
 * returns it unchanged.
 *
 * @since 3.0.0
 *
 * @param value The stored path
 */
export function archiveRelativeFilePath(value: unknown): string {
  if (typeof value !== "string" || !value) {
    return "";
  }
  // "https://api.archedi.it\uploads/temp/x.pdf" is a real stored value: one separator is a backslash
  let path = value.replace(/\\/g, "/").trim();
  const hostEnd = path.indexOf("://");
  if (hostEnd > -1) {
    const afterScheme = path.substring(hostEnd + 3);
    const firstSlash = afterScheme.indexOf("/");
    path = firstSlash > -1 ? afterScheme.substring(firstSlash) : "";
  }
  return path.replace(/^\/+/, "");
}

/**
 * The files of a document as PUBLIC LINKS, for the list a cancellation mail carries.
 *
 * ⚠️ This is the one place the front-end composes a path again, and only for that: `/uploads` is
 * served without a session, which is what makes a link openable by the supplier the mail goes to —
 * the authenticated gate the archive downloads through (@see DocumentArchiveDocsService.downloadFile)
 * would answer 401 in a mailbox. Those links are public and do not expire; signed URLs are planned
 * platform wide and this is the place to change when they land.
 *
 * BOTH ARRAYS ARE READ, `data.document[]` first. The two disagree across the sources: the download
 * dialog and the legacy cancellation dialog both read `data.document[]`, while the archive back-end
 * documents the paths as arriving in `data.documentFile[]`. Reading whichever is populated costs
 * nothing and is the difference between a mail with the links and a mail without them — and a
 * silently empty list is exactly the kind of thing nobody notices.
 *
 * The path is normalized here too, idempotently: a historical row can still carry the malformed shape
 * (doubled host, backslash) that used to produce 404s, and stripping it is a no-op on the clean value
 * the back-end now sends.
 *
 * @since 3.0.0
 *
 * @param row The document
 */
export function archiveLinkableFiles(
  row: DocumentArchiveDocListItem | null | undefined
): DocumentArchiveContractFile[] {
  const files: DocumentArchiveContractFile[] = [];
  const data = row && row.data ? row.data : undefined;
  if (!data) {
    return files;
  }

  const sources = [data.document, data.documentFile];
  for (let s = 0; s < sources.length; s++) {
    const entries = Array.isArray(sources[s]) ? sources[s] : [];
    entries.forEach((entry: any) => {
      // A `documentFile` can also hold bare ids or `{ _id }` objects, which carry no path
      if (!entry || typeof entry !== "object" || !entry.path) {
        return;
      }
      const path = archiveRelativeFilePath(entry.path);
      if (!path) {
        return;
      }
      files.push({
        name: entry.originalName
          ? entry.originalName
          : entry.filename
          ? entry.filename
          : path,
        path,
      });
    });
    // First array that actually yielded something wins: the two are the same files
    if (files.length) {
      return files;
    }
  }

  return files;
}

/**
 * One entry of a checklist over the occurrences of a document.
 *
 * Shared by the delete dialog and by the two contract actions, so that the three never disagree on
 * what the occurrences of a document are. The delete dialog reads the first three members and ignores
 * the rest; the contract dialogs need the rest to decide which rows their action can touch and to say
 * so on the ones it cannot.
 *
 * @since 3.0.0
 *
 * @param row          The row
 * @param docTitle     Localized title of its document type
 * @param areaLabel    Localized label of its area
 * @param contentLabel Localized label of its section
 * @param groupLabel   Heading of its block, when it has one
 */
export function archiveRowOccurrence(
  row: DocumentArchiveDocListItem,
  docTitle: string,
  areaLabel: string,
  contentLabel: string,
  groupLabel?: string | null
): DocumentArchiveContractOccurrence {
  const path: string[] = [];
  if (areaLabel) {
    path.push(areaLabel);
  }
  if (contentLabel) {
    path.push(contentLabel);
  }
  if (groupLabel) {
    path.push(groupLabel);
  }
  return {
    docId: row._id,
    docTitle,
    path: path.join(" > "),
    // Read from the ROW and not from the document type: `contract` is a field of the row, and a type
    // can hold both
    contract: !!row.contract,
    expired: !!row.expired,
    cancellationRequestDate: row.cancellationRequestDate,
    files: archiveLinkableFiles(row),
  };
}

/**
 * Every occurrence of a physical file in the archive, i.e. every row carrying that barcode.
 *
 * Walks the tree, so each occurrence comes with the path that tells it apart from the others: two
 * rows of the same barcode differ by area, section and block, not by title.
 *
 * ONE ENTRY PER ROW, and the tree holds some of them twice: a contract is served both under its own
 * area and inside the aggregated contracts section, as the same row with the same `_id`. Offering it
 * twice would ask the user the same question twice and send the same id twice to the bulk endpoint,
 * which would report it once deleted and once skipped as `not_found`.
 *
 * The blocks hidden as disabled structures ARE walked: an occurrence of the file living under a lift
 * that was switched off is still an occurrence, and leaving it out would delete the document
 * everywhere except there, silently.
 *
 * The rows SET ASIDE with their mapping element (`deleted`, which the tree serves only under a removed
 * element, and only while they are not in the bin) are left out unless `includeRemoved` is asked:
 *  - «Elimina documento» asks for them (EXT-07): the back-end deletes such a row like a live one, as
 *    the legacy does, and its lookup by barcode lists them too. They come back flagged `removed`, so
 *    that the dialog can say so;
 *  - the two contract actions do not: a cancellation or a switch-off on a document set aside with its
 *    element is not something the legacy offers either.
 * Before EXT-07 the bulk endpoint answered such a row as deleted without touching it: offering it would
 * have reported a deletion that never happened.
 *
 * LIMIT: the tree is pruned, so an occurrence living under a document type RETIRED from the template
 * is not in it and cannot be offered — the legacy asked the back-end instead, with a lookup by
 * barcode. If that turns out to matter, the fix is an endpoint, not a wider scan.
 *
 * @since 3.0.0
 *
 * @param areas     The tree
 * @param barCode   `data.barCode` of the document
 * @param lang      Language of the page, for the labels of the paths
 * @param [options] `includeRemoved`: also the rows set aside with their mapping element
 */
export function archiveBarCodeOccurrences(
  areas: DocumentArchiveTreeArea[] | null | undefined,
  barCode: string,
  lang: string,
  options?: { includeRemoved?: boolean }
): DocumentArchiveContractOccurrence[] {
  const includeRemoved = !!(options && options.includeRemoved);
  const occurrences: DocumentArchiveContractOccurrence[] = [];
  /** Where the entry of a document id sits in `occurrences` */
  const positionOfDocId: { [docId: string]: number } = Object.create(null);
  /** Whether the entry kept for a document id came from the aggregated contracts section */
  const keptIsAggregated: { [docId: string]: boolean } = Object.create(null);

  (Array.isArray(areas) ? areas : []).forEach((area) => {
    const contents = area && Array.isArray(area.content) ? area.content : [];
    contents.forEach((content) => {
      const groups = content && Array.isArray(content.groups) ? content.groups : [];
      groups.forEach((group) => {
        const types = group && Array.isArray(group.documents) ? group.documents : [];
        types.forEach((type) => {
          const rows = type && Array.isArray(type.archiveDocs) ? type.archiveDocs : [];
          rows.forEach((row) => {
            if (!row || !row.data || row.data.barCode !== barCode) {
              return;
            }
            // Set aside by the cascade that removed its mapping element: offered only to the delete
            if (row.deleted && !includeRemoved) {
              return;
            }

            const occurrence = archiveRowOccurrence(
              row,
              labelOf(type.translation, lang),
              labelOf(area.translation, lang),
              labelOf(content.translation, lang),
              archiveGroupLabel(group, lang)
            );
            if (row.deleted) {
              occurrence.removed = true;
            }
            // The copy rendered inside the contracts section is the one carrying the fields the
            // back-end resolves for that section, and only there
            const aggregated = isAggregatedCopy(row);
            const position = positionOfDocId[row._id];

            if (position === undefined) {
              positionOfDocId[row._id] = occurrences.length;
              keptIsAggregated[row._id] = aggregated;
              occurrences.push(occurrence);
              return;
            }

            // Same row met again: keep the copy rendered in its own area, whose path names the
            // element the contract hangs from ("Ascensori > Ascensore A") instead of just naming the
            // contracts section
            if (keptIsAggregated[row._id] && !aggregated) {
              occurrences[position] = occurrence;
              keptIsAggregated[row._id] = false;
            }
          });
        });
      });
    });
  });

  return occurrences;
}

/**
 * The section a row is ARCHIVED in, which is the one whose curated list its form was filled from.
 *
 * It differs from the section the row is RENDERED in for the contracts the aggregated contracts
 * section borrowed from another area: opening their form with the list of the contracts section would
 * offer a list the value was never chosen from, and an empty one where that section carries none —
 * which on save would write an empty value over a good one.
 *
 * Told apart by the fields the back-end resolves ONLY inside the contracts section: a row without
 * them is rendered where it is archived, and there is nothing to look for. A row with them is looked
 * up in the rest of the tree, where its other copy — same `_id` — sits under the section it belongs
 * to. A contract archived in the contracts area itself has no other copy and keeps the section it is
 * rendered in.
 *
 * @since 3.0.0
 *
 * @param areas    The tree
 * @param row      The row
 * @param rendered The section it is rendered in, returned when it is also the one it belongs to
 */
export function archiveRowArchivingSection(
  areas: DocumentArchiveTreeArea[] | null | undefined,
  row: DocumentArchiveTreeRow | null | undefined,
  rendered: DocumentArchiveTreeContent
): DocumentArchiveTreeContent {
  if (!row || !isAggregatedCopy(row)) {
    return rendered;
  }

  const tree = Array.isArray(areas) ? areas : [];
  for (let a = 0; a < tree.length; a++) {
    const contents = tree[a] && Array.isArray(tree[a].content) ? tree[a].content : [];
    for (let c = 0; c < contents.length; c++) {
      const groups = contents[c] && Array.isArray(contents[c].groups) ? contents[c].groups : [];
      for (let g = 0; g < groups.length; g++) {
        const types = groups[g] && Array.isArray(groups[g].documents) ? groups[g].documents : [];
        for (let t = 0; t < types.length; t++) {
          const rows = types[t] && Array.isArray(types[t].archiveDocs) ? types[t].archiveDocs : [];
          for (let r = 0; r < rows.length; r++) {
            if (rows[r] && rows[r]._id === row._id && !isAggregatedCopy(rows[r])) {
              return contents[c];
            }
          }
        }
      }
    }
  }

  return rendered;
}

/**
 * The area a section belongs to.
 *
 * Identity is the test: `archiveRowArchivingSection` returns the very object the tree holds, and a
 * section carries no reference to its area to match on.
 *
 * @since 3.0.0
 *
 * @param areas    The tree
 * @param content  The section to locate
 * @param fallback The area to answer when it is not found, so a caller always has one
 */
export function archiveAreaOfContent(
  areas: DocumentArchiveTreeArea[] | null | undefined,
  content: DocumentArchiveTreeContent,
  fallback: DocumentArchiveTreeArea
): DocumentArchiveTreeArea {
  const tree = Array.isArray(areas) ? areas : [];
  for (let a = 0; a < tree.length; a++) {
    const contents = tree[a] && Array.isArray(tree[a].content) ? tree[a].content : [];
    for (let c = 0; c < contents.length; c++) {
      if (contents[c] === content) {
        return tree[a];
      }
    }
  }

  return fallback;
}

/**
 * Values of the `SelectNew` providers for a section.
 *
 * Only what the archive tree actually carries: `content.dropdown` gives the curated list of the
 * section, and its `source` says which provider it feeds — the supplier names of «Valutazione
 * fornitori» too, once the back-end serves them (FE-M6). A provider without a list renders an empty
 * widget, where a name can still be typed.
 *
 * @since 3.0.0
 *
 * @param content The section the form was filled from
 */
export function archiveOptionLists(
  content: DocumentArchiveTreeContent | null | undefined
): { [source: string]: string[] } {
  const lists: { [source: string]: string[] } = {};
  const dropdown = content ? content.dropdown : undefined;
  if (dropdown && dropdown.source && Array.isArray(dropdown.values)) {
    lists[dropdown.source] = dropdown.values;
  }
  return lists;
}

/**
 * Where a row sits in the tree of its property, found by its id: what the general search needs to
 * open the menu of a row it got from a list, with the dialogs the archive page opens.
 *
 * The copy rendered in the row's OWN section is preferred over the copy the aggregated contracts
 * section borrowed: its section is the one the row's form was filled from, and its block names the
 * element the row hangs from. A contract archived in the contracts section itself has no other copy,
 * so there the aggregated one is the answer.
 *
 * Rows the cascade deleted are not offered: no action applies to them but reading, and the general
 * search never lists them.
 *
 * @since 3.0.0
 *
 * @param areas The tree of the property
 * @param docId `_id` of the row
 * @returns The row and its place, or null when the tree does not hold it
 */
export function findArchiveRowLocation(
  areas: DocumentArchiveTreeArea[] | null | undefined,
  docId: string
): ArchiveRowLocation | null {
  if (typeof docId !== "string" || docId === "") {
    return null;
  }

  let aggregated: ArchiveRowLocation | null = null;
  const tree = Array.isArray(areas) ? areas : [];
  for (let a = 0; a < tree.length; a++) {
    const area = tree[a];
    const contents = area && Array.isArray(area.content) ? area.content : [];
    for (let c = 0; c < contents.length; c++) {
      const content = contents[c];
      const groups = content && Array.isArray(content.groups) ? content.groups : [];
      for (let g = 0; g < groups.length; g++) {
        const group = groups[g];
        const types = group && Array.isArray(group.documents) ? group.documents : [];
        for (let t = 0; t < types.length; t++) {
          const templateDoc = types[t];
          const rows =
            templateDoc && Array.isArray(templateDoc.archiveDocs) ? templateDoc.archiveDocs : [];
          for (let r = 0; r < rows.length; r++) {
            const row = rows[r];
            if (!row || row._id !== docId || row.deleted === true) {
              continue;
            }
            const location: ArchiveRowLocation = { row, templateDoc, area, content, group };
            if (!isAggregatedCopy(row)) {
              return location;
            }
            if (!aggregated) {
              aggregated = location;
            }
          }
        }
      }
    }
  }

  return aggregated;
}

/**
 * The occurrence of ONE row, with the title and the place the tree gives it: what «Elimina documento»
 * offers on a filed row without a code, which has no other occurrence to look for. The one such row
 * is a VIRTUAL document (`mockup`, FE-M5): filed as soon as it carries a form, never with a code.
 *
 * Placed through @see findArchiveRowLocation, so a virtual contract is named by the section it is
 * archived in and not by the contracts section that borrows it. A row the tree does not hold comes
 * back alone, with no title and no place, as the delete offered it before.
 *
 * @since 3.0.0
 *
 * @param areas The tree
 * @param row   The row
 * @param lang  Language of the page, for the labels of the place
 */
export function archiveRowOccurrenceInTree(
  areas: DocumentArchiveTreeArea[] | null | undefined,
  row: DocumentArchiveTreeRow,
  lang: string
): DocumentArchiveContractOccurrence {
  const location = findArchiveRowLocation(areas, row._id);
  if (!location) {
    return archiveRowOccurrence(row, "", "", "");
  }
  return archiveRowOccurrence(
    row,
    labelOf(location.templateDoc ? location.templateDoc.translation : undefined, lang),
    labelOf(location.area ? location.area.translation : undefined, lang),
    labelOf(location.content ? location.content.translation : undefined, lang),
    archiveGroupLabel(location.group, lang)
  );
}
